import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Mail, Phone } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const EMAIL = 'Info@scorpionfunding.com';
const PHONE = '347-864-6712';

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const send = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill all fields.');
      return;
    }
    setSending(true);
    // Save to backend for record
    try {
      await axios.post(`${API}/leads`, {
        full_name: form.full_name,
        email: form.email,
        message: form.message,
        funding_type: 'Chat Inquiry',
      });
    } catch (err) {
      // non-blocking
      console.error(err);
    }
    // Open user mail client
    const subject = encodeURIComponent(`Scorpion Funding Inquiry — ${form.full_name}`);
    const body = encodeURIComponent(
      `Name: ${form.full_name}\nEmail: ${form.email}\n\n${form.message}`
    );
    window.location.href = `mailto:${EMAIL}?subject=${subject}&body=${body}`;
    setSending(false);
    toast.success('Opening your email client...');
    setForm({ full_name: '', email: '', message: '' });
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 lg:w-16 lg:h-16 bg-[#c2a978] hover:bg-[#d9c5a0] text-black rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(201,162,74,0.35)] transition-colors duration-300"
        aria-label={open ? 'Close chat' : 'Open chat'}
        data-testid="chat-toggle"
      >
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.4 }}
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <MessageSquare size={22} strokeWidth={1.5} />}
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-4 lg:right-6 z-50 w-[calc(100vw-32px)] max-w-[380px] bg-[#0a0a0a] border border-[#c2a978]/30 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="p-6 border-b border-[#c2a978]/15">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-[#c2a978] rounded-full animate-pulse" />
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#c2a978]">
                  Online · Reply within 1 hour
                </div>
              </div>
              <h3 className="font-serif text-2xl text-[#f5f5f0] mt-3 leading-tight">
                Reach a <span className="italic text-[#c2a978]">principal</span>.
              </h3>
              <p className="text-[#8a8a8a] text-xs mt-2 leading-relaxed">
                Send a note — it will route directly to our intake desk.
              </p>
            </div>

            {/* Quick contact */}
            <div className="px-6 py-4 border-b border-[#c2a978]/10 flex flex-col gap-2">
              <a
                href={`mailto:${EMAIL}`}
                className="flex items-center gap-3 text-xs text-[#f5f5f0] hover:text-[#c2a978] transition-colors"
                data-testid="chat-email-link"
              >
                <Mail size={14} className="text-[#c2a978]" />
                {EMAIL}
              </a>
              <a
                href={`tel:${PHONE.replace(/\D/g, '')}`}
                className="flex items-center gap-3 text-xs text-[#f5f5f0] hover:text-[#c2a978] transition-colors"
                data-testid="chat-phone-link"
              >
                <Phone size={14} className="text-[#c2a978]" />
                {PHONE}
              </a>
            </div>

            {/* Form */}
            <form onSubmit={send} className="p-6 space-y-4" data-testid="chat-form">
              <input
                type="text"
                placeholder="Your name"
                value={form.full_name}
                onChange={set('full_name')}
                required
                className="w-full bg-transparent border-b border-[#c2a978]/20 focus:border-[#c2a978] outline-none text-[#f5f5f0] text-sm py-2 placeholder:text-[#5a5a5a] transition-colors"
                data-testid="chat-name"
              />
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={set('email')}
                required
                className="w-full bg-transparent border-b border-[#c2a978]/20 focus:border-[#c2a978] outline-none text-[#f5f5f0] text-sm py-2 placeholder:text-[#5a5a5a] transition-colors"
                data-testid="chat-email"
              />
              <textarea
                placeholder="How can we help?"
                rows={3}
                value={form.message}
                onChange={set('message')}
                required
                className="w-full bg-transparent border-b border-[#c2a978]/20 focus:border-[#c2a978] outline-none text-[#f5f5f0] text-sm py-2 placeholder:text-[#5a5a5a] resize-none transition-colors"
                data-testid="chat-message"
              />
              <button
                type="submit"
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 py-3 bg-[#c2a978] hover:bg-[#d9c5a0] text-black text-[11px] tracking-[0.3em] uppercase transition-colors disabled:opacity-60"
                data-testid="chat-send"
              >
                {sending ? 'Sending...' : (<>Send Message <Send size={13} /></>)}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
